import { useMemo } from "react";

import FilterPanel from "components/partials/filter-panel/filter-panel";
import ColumnOptions from "components/partials/column-options/column-options";

import { mapDictionaryItemsToMultiSelectOptions, useGetPopulationTypesQuery } from "hooks/api/dictionary";

import { FiltersToApplyOnClientReviewsTable } from "containers/client/components/client-reviews-table/client-reviews-table.types";
import { UserTableLayout } from "hooks/api/use-user-layout";
import { ColumnOption } from "types/table";
import { Filter, FilterOption, FiltersStateOnTable } from "types/filters";

import { CLIENT_REVIEWS_TABLE_FILTERS_DATA } from "containers/client/components/client-reviews-table/client-reviews-table.consts";

interface FiltersOnClientReviewsTableProps
  extends FiltersStateOnTable<FiltersToApplyOnClientReviewsTable> {
  tableConfigKey: string;
  tableColumnsConfig: UserTableLayout;
  columnOptions: ColumnOption[];
  onToggleColumn: (columnId: string) => void;
  showTouchpointTypeFilter?: boolean;
  isFetching?: boolean;
}

const FiltersOnClientReviewsTable = ({
  filtersToApply,
  setFiltersToApply,
  tableConfigKey,
  tableColumnsConfig,
  columnOptions,
  onToggleColumn,
  showTouchpointTypeFilter = true,
  isFetching = false,
}: FiltersOnClientReviewsTableProps) => {
  const { data: populationTypes = [] } = useGetPopulationTypesQuery();

  const filtersToDisplay = useMemo<Filter[]>(
    () => [
      ...CLIENT_REVIEWS_TABLE_FILTERS_DATA,
      {
        filterId: "populationType",
        title: "Population type",
        options: mapDictionaryItemsToMultiSelectOptions(populationTypes).map((option) => ({
          name: String(option.value),
          description: option.label,
        })),
      },
    ],
    [populationTypes],
  );

  const handleFilterChange = (filter: Filter, option: FilterOption) => {
    const filterKey = filter.filterId as keyof FiltersToApplyOnClientReviewsTable;
    const currentValues = (filtersToApply[filterKey] as string[]) || [];
    const isSelected = currentValues.includes(option.name);

    setFiltersToApply({
      ...filtersToApply,
      [filterKey]: isSelected
        ? currentValues.filter((item) => item !== option.name)
        : [...currentValues, option.name],
    });
  };

  return (
    <>
      {showTouchpointTypeFilter && (
        <FilterPanel
          filters={filtersToDisplay}
          currentFilters={filtersToApply}
          onFilterChange={handleFilterChange}
          isFetching={isFetching}
        />
      )}

      <ColumnOptions
        tableConfigKey={tableConfigKey}
        tableColumnsConfig={tableColumnsConfig}
        columnOptions={columnOptions}
        onToggleColumn={onToggleColumn}
      />
    </>
  );
};

export default FiltersOnClientReviewsTable;
